import React, { createContext, useContext, useMemo, useState } from 'react';

import type { CyphieMode } from '../state/LabsAtoms';

export type MessageRole = 'user' | 'assistant';

export type MessageMetadata = {
	rawCypherQuery?: string;
	cyphieMode?: CyphieMode;
};

export type Message = {
	id: string;
	role: MessageRole;
	content: string;
	metadata?: MessageMetadata;
};

type AsideState = {
	isOpen: boolean;
	content: React.ReactNode | null;
	owner?: string;
	messages: Message[];
};

type OpenArgs = {
	content: React.ReactNode;
	owner?: string;
};

export type AsideController = {
	isOpen: boolean;
	content: React.ReactNode | null;
	owner?: string;
	messages: Message[];
	open: (args: OpenArgs) => void;
	close: () => void;
	closeIfOwned: (owner: string) => void;
	toggle: (args: OpenArgs) => void;
	setContent: (content: React.ReactNode, owner?: string) => void;
	addMessage: (message: Message) => void;
};

const AsideContext = createContext<AsideController | undefined>(undefined);

type AsideProviderProps = {
	children: React.ReactNode;
};

export const AsideProvider = ({ children }: AsideProviderProps): JSX.Element => {
	const [state, setState] = useState<AsideState>({
		isOpen: false,
		content: null,
		messages: [],
	});

	const controller = useMemo<AsideController>(
		() => ({
			isOpen: state.isOpen,
			content: state.content,
			owner: state.owner,
			messages: state.messages,
			open: ({ content, owner }: OpenArgs) =>
				setState((s) => ({ ...s, isOpen: true, content, owner })),
			close: () => setState((s) => ({ ...s, isOpen: false })),
			closeIfOwned: (owner: string) =>
				setState((s) => (s.owner === owner ? { ...s, isOpen: false } : s)),
			toggle: ({ content, owner }: OpenArgs) =>
				setState((s) => {
					const shouldClose = s.isOpen && s.owner === owner;
					return {
						...s,
						isOpen: !shouldClose,
						content: shouldClose ? s.content : content,
						owner,
					};
				}),
			setContent: (content: React.ReactNode, owner?: string) =>
				setState((s) => ({ ...s, content, owner })),
			addMessage: (message: Message) =>
				setState((s) => ({ ...s, messages: [...s.messages, message] })),
		}),
		[state.isOpen, state.content, state.owner, state.messages],
	);

	return <AsideContext.Provider value={controller}>{children}</AsideContext.Provider>;
};

export const useAsideController = (): AsideController => {
	const ctx = useContext(AsideContext);
	if (ctx === undefined || ctx === null) {
		throw new Error('useAsideController must be used within AsideProvider');
	}
	return ctx;
};
